'use client'

import React from 'react'
import { HTMLMotionProps, motion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
  Avatar,
  AvatarFallback,
  Label,
  LoadingSpinner,
  Skeleton,
} from '@/components/ui'

type AvatarCardProps = React.ComponentPropsWithoutRef<'div'> &
  HTMLMotionProps<'div'> & {
    shortCode: string
    label: string
    actionLabel?: string
    isLoading?: boolean
    disabled?: boolean
  }

export const AvatarCard = ({
  className,
  shortCode,
  label,
  actionLabel,
  isLoading = false,
  disabled,
  ...restProps
}: AvatarCardProps) => {
  return (
    <motion.div
      className={cn(
        'group flex w-full select-none items-center justify-between gap-4 rounded-lg border border-gray-100 border-gray-100/80 bg-white p-4 shadow-card transition-colors dark:border-gray-700',
        {
          'cursor-pointer': !disabled && !isLoading,
          'pointer-events-none opacity-50': disabled,
        },
        className,
      )}
      {...restProps}
    >
      <div className="flex items-center gap-4">
        <Avatar>
          <AvatarFallback className="uppercase group-hover:bg-blue-50 group-hover:text-blue-600 group-focus:bg-blue-50 group-focus:text-blue-600">
            {shortCode}
          </AvatarFallback>
        </Avatar>
        <span>{label}</span>
      </div>

      <div className="flex items-center gap-2 pr-2">
        {actionLabel && !isLoading && !disabled && (
          <Label className="translate-x-6 !text-sm text-gray-500 opacity-0 transition-all group-hover:translate-x-0 group-hover:text-gray-950 group-hover:opacity-100 group-focus:translate-x-0 group-focus:text-gray-950 group-focus:opacity-100">
            {actionLabel}
          </Label>
        )}

        {isLoading && <LoadingSpinner />}
        {!isLoading && <ChevronRight className="size-5" strokeWidth={1.5} />}
      </div>
    </motion.div>
  )
}

type AvatarCardSkeletonProps = React.ComponentPropsWithoutRef<'div'> &
  HTMLMotionProps<'div'>

export const AvatarCardSkeleton = ({
  className,
  ...restProps
}: AvatarCardSkeletonProps) => {
  return (
    <motion.div
      className={cn(
        'flex w-full select-none items-center justify-between gap-4 rounded-lg border border-gray-100 bg-white p-4 dark:border-gray-700',
        className,
      )}
      {...restProps}
    >
      <div className="flex items-center gap-4">
        <Skeleton className="size-10 rounded-full" />
        <Skeleton className="h-3 w-24 rounded-full" />
      </div>

      <div className="flex items-center gap-2">
        <Skeleton className="h-3 w-16 rounded-full" />
        <ChevronRight
          className="size-5 animate-pulse !text-gray-300"
          strokeWidth={1.5}
        />
      </div>
    </motion.div>
  )
}
